/**
 * Bracket papillon (specs/01 §6) — composant legacy porté : 9 colonnes
 * (16es → finale → 16es), auto-dimensionné (scale), liens SVG entre cartes.
 * Slots officiels remplis au fil des qualifications ; slots indéterminés :
 * les 2 équipes les plus probables avec leur % (Monte Carlo), typo atténuée.
 */
import bracketJson from "../../data/bracket.json";
import type { OfficialState } from "../../app/officialStore.ts";
import type { SimulationOutput } from "../../engine/montecarlo.ts";
import { esc, flagSpan, teamName } from "../html.ts";
import { fmtShortDateTime, fmtPct } from "../../app/format.ts";
import { t } from "../../app/i18n.ts";

const LAYOUT = bracketJson.layout as unknown as {
  columns: (string[] | "C")[];
  labels: string[];
  designWidth: number;
};
const NODES = bracketJson.nodes as unknown as Record<
  string,
  { round: string; city: string; winnerTo: [string, string] | null }
>;

interface SlotView {
  team: string | null;
  token: string;
  score: number | null;
  pens: number | null;
  winner: boolean | null;
}

function slotView(state: OfficialState, nodeId: string, side: "a" | "b"): SlotView {
  const id = Number(nodeId.slice(1));
  const resolved = state.bracket[nodeId];
  const view = state.byId.get(id);
  const team = (side === "a" ? resolved?.a : resolved?.b) ?? null;
  const token = side === "a" ? (view?.match.home ?? "?") : (view?.match.away ?? "?");
  const score = view?.displayScore ? view.displayScore[side === "a" ? 0 : 1] : null;
  const pens = view?.penalties ? view.penalties.score[side === "a" ? 0 : 1] : null;
  const winner =
    resolved?.winner == null || team === null ? null : resolved.winner === team;
  return { team, token, score, pens, winner };
}

function probableHtml(mc: SimulationOutput | null, nodeId: string, side: "a" | "b"): string {
  const dist = mc?.slotDistribution[nodeId]?.[side];
  if (!dist) return "";
  const top = Object.entries(dist).slice(0, 2);
  if (top.length === 0) return "";
  return `<span class="probable">${top
    .map(([code, p]) => `${esc(code)} ${esc(fmtPct(p, 0))}`)
    .join(" · ")}</span>`;
}

function rowHtml(slot: SlotView, mc: SimulationOutput | null, nodeId: string, side: "a" | "b"): string {
  if (slot.team) {
    const cls = slot.winner === false ? "tr lose" : "tr";
    const score =
      slot.score !== null
        ? `<span class="pt">${slot.score}${slot.pens !== null ? `<span class="pens">(${slot.pens})</span>` : ""}</span>`
        : "";
    return `<div class="${cls}" data-t="${esc(slot.team)}" title="${esc(teamName(slot.team))}">${flagSpan(slot.team)}${score}</div>`;
  }
  return `<div class="tr open"><span class="ph"></span><span class="cd dim">${esc(slot.token)}</span>${probableHtml(mc, nodeId, side)}</div>`;
}

function cardHtml(state: OfficialState, mc: SimulationOutput | null, nodeId: string, big = false): string {
  const id = Number(nodeId.slice(1));
  const view = state.byId.get(id);
  const node = NODES[nodeId];
  const done = view?.status === "finished";
  const kick = view ? fmtShortDateTime(view.match.utcDateTime) : "";
  const a = slotView(state, nodeId, "a");
  const b = slotView(state, nodeId, "b");
  // Cliquable (→ fiche match) UNIQUEMENT quand les deux équipes sont connues :
  // sinon l'overlay n'a rien à montrer → fausse affordance (cf. CLAUDE.md).
  const open = a.team != null && b.team != null ? ` data-match="${id}"` : "";
  return `
    <div class="m ${big ? "big" : ""} ${done ? "done" : ""}" data-m="${nodeId}"${open}>
      <div class="mh">${esc(node?.city ?? "")} · ${esc(kick)}</div>
      ${rowHtml(a, mc, nodeId, "a")}
      ${rowHtml(b, mc, nodeId, "b")}
    </div>`;
}

export function renderBracket(el: HTMLElement, state: OfficialState, mc: SimulationOutput | null): void {
  const labels = LAYOUT.labels.map((l) => `<div class="clab">${esc(l)}</div>`).join("");
  const cols = LAYOUT.columns
    .map((col) => {
      if (col === "C") {
        return `<div class="bcenter">
          <div class="bclab">${t("bracket.final")}</div>
          ${cardHtml(state, mc, "M104", true)}
          <div class="bclab">${t("bracket.thirdPlace")}</div>
          ${cardHtml(state, mc, "M103")}
        </div>`;
      }
      return `<div class="col">${col.map((id) => cardHtml(state, mc, id)).join("")}</div>`;
    })
    .join("");
  el.innerHTML = `
    <h2 class="sec-title">${t("bracket.title")}</h2>
    <div class="bk"><div class="bkIn">
      <div class="blabels">${labels}</div>
      <div class="bcols"><svg class="lnk" aria-hidden="true"></svg>${cols}</div>
    </div></div>`;
  fitBracket(el);
  drawLinks(el);
}

/** Legacy fitBracket: scale to available width AND viewport height. */
export function fitBracket(el: HTMLElement): void {
  const wrap = el.querySelector<HTMLElement>(".bk");
  const inner = el.querySelector<HTMLElement>(".bkIn");
  if (!wrap || !inner) return;
  inner.style.width = `${LAYOUT.designWidth}px`;
  inner.style.transform = "scale(1)";
  const naturalH = inner.offsetHeight;
  const availW = wrap.clientWidth || LAYOUT.designWidth;
  const sticky = document.getElementById("stickyWrap")?.offsetHeight ?? 0;
  const availH = Math.max(280, window.innerHeight - sticky - 80);
  const fitScale = Math.min(1, availW / LAYOUT.designWidth, availH / naturalH);
  // Sur petit écran, réduire le tableau à la largeur dispo le rend illisible
  // (~0,30 → trigrammes ~3px). On plante un plancher d'échelle et on laisse .bk
  // défiler horizontalement : le contenu reste lisible, la page ne déborde pas.
  const minScale = window.innerWidth < 620 ? 0.6 : 0;
  const scale = Math.max(minScale, fitScale);
  inner.style.transform = `scale(${scale})`;
  inner.style.transformOrigin = "top left";
  wrap.style.height = `${Math.ceil(naturalH * scale)}px`;
  // scroll X seulement quand l'échelle est plafonnée (sinon rien à défiler) ;
  // overflow-y reste masqué (hauteur calée), comportement desktop inchangé.
  wrap.style.overflowX = scale > fitScale + 0.001 ? "auto" : "hidden";
  drawLinks(el, scale);
}

/** Legacy links(): orthogonal connectors between each match and its successor. */
function drawLinks(el: HTMLElement, scale = 1): void {
  const host = el.querySelector<HTMLElement>(".bcols");
  const svg = el.querySelector<SVGElement>("svg.lnk");
  if (!host || !svg) return;
  const r0 = host.getBoundingClientRect();
  let d = "";
  host.querySelectorAll<HTMLElement>(".m").forEach((card) => {
    const nodeId = card.dataset.m;
    if (!nodeId) return;
    const next = NODES[nodeId]?.winnerTo;
    if (!next) return;
    const target = host.querySelector<HTMLElement>(`[data-m="${next[0]}"]`);
    if (!target) return;
    const a = card.getBoundingClientRect();
    const b = target.getBoundingClientRect();
    const s = scale || 1;
    const aL = (a.left - r0.left) / s;
    const aR = (a.right - r0.left) / s;
    const aY = (a.top + a.height / 2 - r0.top) / s;
    const bL = (b.left - r0.left) / s;
    const bR = (b.right - r0.left) / s;
    const bY = (b.top + b.height / 2 - r0.top) / s;
    const [x1, x2] = (bL + bR) / 2 > (aL + aR) / 2 ? [aR, bL] : [aL, bR];
    const mx = (x1 + x2) / 2;
    d += `M${Math.round(x1)} ${Math.round(aY)}H${Math.round(mx)}V${Math.round(bY)}H${Math.round(x2)}`;
  });
  svg.innerHTML = `<path d="${d}" fill="none" stroke="var(--color-border-secondary)" stroke-width="1"/>`;
}
